import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Typography,
} from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "src/shared/context/useAuth";
import CallMadeIcon from "@mui/icons-material/CallMade";
import {
  AutocompleteElement,
  FormContainer,
  TextFieldElement,
  useForm,
} from "react-hook-form-mui";
import { useParams } from "react-router-dom";
import useGetAssignUsers from "../data-access/useGetAssignUsers";
import InfoMessage from "src/shared/components/InfoMessage";
import { LoadingButton } from "@mui/lab";
import { yupResolver } from "@hookform/resolvers/yup";
import { assignValidationSchema } from "../data-access/assignValidationSchema";
import { enqueueSnackbar } from "notistack";
import useAssignCase from "../data-access/useAssignCase";
import { MessageVariant } from "src/shared/enums/messageTypes/messageTypes";
import ReplayIcon from "@mui/icons-material/Replay";
import useGetRefreshAadeData from "../data-access/useGetRefreshAadeData";

export interface AssignFormValues {
  chamberUser: { value: string; label: string };
  message: string | undefined;
}

export default function CaseActionBar({
  caseCanBeAssigned,
}: {
  caseCanBeAssigned: Boolean;
}) {
  const { t } = useTranslation();
  const { isOrgAdmin } = useAuth();

  const [openAssign, setOpenAssign] = React.useState<boolean>(false);
  const validationSchema = assignValidationSchema();

  const formContext = useForm<AssignFormValues>({
    resolver: yupResolver(validationSchema),
  });

  const { caseId } = useParams<{ caseId: string }>();

  const { data: chamberUserData, isLoading: isLoadingChamberUsers } =
    useGetAssignUsers({ case_id: parseInt(caseId), enabled: openAssign });

  const { mutate: assignUser, isLoading: isLoadingUserAssignment } =
    useAssignCase(setOpenAssign);

  const { mutate: refreshAadeData, isLoading: isLoadingUpdateOnAadeData } =
    useGetRefreshAadeData();

  return (
    <>
      <Paper
        sx={{
          position: { xs: "relative", md: "sticky" },
          top: "9vh",
          zIndex: "50",
          display: "flex",
          flexWrap: "wrap",
          padding: "1rem",
          gap: "1rem",
        }}
      >
        <Button
          variant="contained"
          onClick={() => {
            window.print();
          }}
        >
          {t("Εκτύπωση")}{" "}
        </Button>

        {isOrgAdmin && caseCanBeAssigned && (
          <Button variant="contained" onClick={() => setOpenAssign(true)}>
            {t("Ανάθεση σε χρήστη της Υ.Γ.Ε.ΜΗ.")}
          </Button>
        )}
        {caseCanBeAssigned && (
          <LoadingButton
            variant="contained"
            startIcon={<ReplayIcon />}
            loading={isLoadingUpdateOnAadeData}
            onClick={() => refreshAadeData(parseInt(caseId))}
          >
            {t("Ενημέρωση Στοιχείων απο ΑΑΔΕ")}
          </LoadingButton>
        )}
        <Button
          variant="contained"
          endIcon={<CallMadeIcon />}
          onClick={() =>
            window.open(
              import.meta.env.VITE_REGISTRY_URL,
              "_blank",
              "noopener,noreferrer",
            )
          }
        >
          {`${t("Μετάβαση στο")} Registry`}
        </Button>
      </Paper>
      <Dialog
        open={openAssign}
        onClose={() => setOpenAssign(false)}
        sx={{ padding: "1rem" }}
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h5">
            {t("Ανάθεση Υπόθεσης σε χρήστη Υ.Γ.Ε.ΜΗ.")}
          </Typography>
        </DialogTitle>
        <FormContainer
          onError={(formError) => {
            Object.entries(formError).forEach(([key, value]) => {
              enqueueSnackbar(value?.message, {
                variant: "error",
              });
            });
          }}
          onSuccess={(data: AssignFormValues) => {
            assignUser({
              case_id: caseId ? parseInt(caseId) : null,
              chamber_user: parseInt(data.chamberUser.value),
              message: data.message,
            });
          }}
          formContext={formContext}
        >
          <DialogContent sx={{ padding: "1rem" }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <InfoMessage
                variant={MessageVariant.Info}
                message={t(
                  "Αναθέστε την υπόθεση σε υπάλληλο της Υ. Γ.Ε.ΜΗ.. Μπορείτε να του αποστείλετε και ένα σχετικό μήνυμα.",
                )}
              />
              <AutocompleteElement
                label={t("Χρήστης Υ.Γ.Ε.ΜΗ.")}
                options={chamberUserData?.data.chamberUsers}
                autocompleteProps={{
                  isOptionEqualToValue: (option, value) => {
                    return option.value === value.value;
                  },
                }}
                required
                name="chamberUser"
                loading={isLoadingChamberUsers}
              />
              <TextFieldElement
                rows={4}
                name="message"
                multiline
                label={t("Τυχόν παρατηρήσεις ή σχόλιά σας προς τον αποδέκτη")}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ padding: "1rem" }}>
            <Button
              variant="contained"
              color="error"
              onClick={() => setOpenAssign(false)}
            >
              {t("Ακύρωση")}
            </Button>
            <LoadingButton
              loading={isLoadingUserAssignment}
              type={"submit"}
              variant="contained"
              color="primary"
            >
              {t("Ανάθεση")}
            </LoadingButton>
          </DialogActions>
        </FormContainer>
      </Dialog>
    </>
  );
}
